import React, { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Download, FileText, Loader2 } from 'lucide-react';
import MultiPicker from './MultiPicker';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';

const fmtInr = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const iso = (offset) => { const dt = new Date(); dt.setDate(dt.getDate() + offset); return dt.toISOString().slice(0, 10); };
const dayLabel = (isoStr) => {
  try { return new Date(isoStr + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); }
  catch { return isoStr; }
};

const PRESETS = [
  { id: 'today', label: 'Today', params: () => ({ from_date: iso(0), to_date: iso(0) }) },
  { id: 'yesterday', label: 'Yesterday', params: () => ({ from_date: iso(-1), to_date: iso(-1) }) },
  { id: 'last7', label: 'Last 7 days', params: () => ({ from_date: iso(-6), to_date: iso(0) }) },
  { id: 'last14', label: 'Last 14 days', params: () => ({ from_date: iso(-13), to_date: iso(0) }) },
  { id: 'last30', label: 'Last 30 days', params: () => ({ from_date: iso(-29), to_date: iso(0) }) },
  { id: 'last90', label: 'Last 90 days', params: () => ({ from_date: iso(-89), to_date: iso(0) }) },
];

// Deterministic palette for salesperson series (kept stable across renders)
const SERIES_COLORS = [
  '#059669', '#7c3aed', '#0284c7', '#dc2626', '#d97706',
  '#0891b2', '#c026d3', '#65a30d', '#e11d48', '#4f46e5',
  '#0d9488', '#b45309',
];
const colorFor = (name, idx) => name === 'Others' ? '#94a3b8' : SERIES_COLORS[idx % SERIES_COLORS.length];

// Custom tooltip content
const ChartTooltip = ({ active, payload, groups }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const grouped = Array.isArray(groups) && groups.length > 0;
  return (
    <div className="bg-white border border-slate-200 shadow-lg rounded-md px-3 py-2 text-xs min-w-[180px]">
      <p className="font-semibold text-slate-800">{dayLabel(p.date)} <span className="text-slate-400 font-normal">({p.date})</span></p>
      {grouped ? (
        <>
          <p className="text-emerald-700 tabular-nums font-bold mb-1">Total {fmtInr(p.total)}</p>
          <div className="space-y-0.5 max-h-40 overflow-auto">
            {groups.map((g, i) => (
              <div key={g} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-1.5 text-slate-600 truncate max-w-[130px]">
                  <span className="inline-block w-2 h-2 rounded-sm" style={{ background: colorFor(g, i) }} />
                  {g}
                </span>
                <span className="tabular-nums text-slate-800">{fmtInr(p[g] || 0)}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <p className="text-emerald-700 tabular-nums font-bold">{fmtInr(p.total)}</p>
          <p className="text-slate-500 text-[10px]">{p.count} payment{p.count === 1 ? '' : 's'}</p>
        </>
      )}
    </div>
  );
};

const CHART_TYPES = [
  { id: 'area', label: 'Area', icon: '📈' },
  { id: 'line', label: 'Line', icon: '📉' },
  { id: 'bar', label: 'Bar', icon: '📊' },
];

export const PaymentsTrendModal = ({ open, onOpenChange }) => {
  const [preset, setPreset] = useState('last7');
  const [custom, setCustom] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [chartType, setChartType] = useState('area');
  const [includeClientType, setIncludeClientType] = useState([]);
  const [includeSalesperson, setIncludeSalesperson] = useState([]);
  const [groupBy, setGroupBy] = useState(''); // '' | 'salesperson'
  const [topN, setTopN] = useState(5); // 5 | 10 | 0 (all)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let params;
      if (custom && customFrom && customTo) params = { from_date: customFrom, to_date: customTo };
      else params = (PRESETS.find((p) => p.id === preset) || PRESETS[0]).params();
      if (includeClientType.length) params.include_client_type = includeClientType.join(',');
      if (includeSalesperson.length) params.include_salesperson = includeSalesperson.join(',');
      if (groupBy) params.group_by = groupBy;
      if (groupBy === 'salesperson' && topN > 0) params.top_n = topN;
      const r = await api.get('/books/payments-trend', { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load trend');
    } finally { setLoading(false); }
  }, [preset, custom, customFrom, customTo, includeClientType, includeSalesperson, groupBy, topN]);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  // Export PNG via SVG-to-canvas
  const exportPng = async () => {
    const svg = document.querySelector('[data-testid="payments-trend-chart"] svg');
    if (!svg) return toast.error('Chart not ready');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const bbox = svg.getBoundingClientRect();
    canvas.width = bbox.width * 2; canvas.height = bbox.height * 2;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `payments-trend-${data.window.from}-to-${data.window.to}.png`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const exportCsv = () => {
    if (!data) return;
    const groups = data.groups || [];
    const grouped = groups.length > 0;
    const lines = [];
    if (grouped) {
      lines.push(['Date', ...groups, 'Total (INR)', 'Payments'].join(','));
      data.series.forEach((s) => {
        const row = [s.date, ...groups.map((g) => Math.round(s[g] || 0)), Math.round(s.total || 0), s.count || 0];
        lines.push(row.join(','));
      });
      const totals = groups.map((g) => Math.round(data.series.reduce((acc, s) => acc + (s[g] || 0), 0)));
      lines.push(['', ...totals, Math.round(data.summary.total), data.summary.count].join(','));
    } else {
      lines.push('Date,Total (INR),Payments');
      data.series.forEach((s) => lines.push(`${s.date},${Math.round(s.total)},${s.count}`));
      lines.push(`,${Math.round(data.summary.total)},${data.summary.count}`);
    }
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `payments-trend-${data.window.from}-to-${data.window.to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const s = data?.summary;
  const avg = s?.avg_per_day || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl" data-testid="payments-trend-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">📈 Payments Received — Daily Trend</DialogTitle>
          {data && <p className="text-[11px] text-slate-500 mt-0.5">{data.window.from} → {data.window.to} · sourced from Zoho Books customer payments</p>}
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((pr) => (
            <button key={pr.id} type="button" onClick={() => { setCustom(false); setPreset(pr.id); }}
              data-testid={`trend-preset-${pr.id}`}
              className={`text-[11px] px-2.5 py-1 rounded-md border transition ${!custom && preset === pr.id ? 'bg-violet-600 text-white border-violet-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{pr.label}</button>
          ))}
          <button type="button" onClick={() => setCustom(true)} data-testid="trend-preset-custom"
            className={`text-[11px] px-2.5 py-1 rounded-md border transition ${custom ? 'bg-violet-600 text-white border-violet-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Custom</button>
          {custom && (
            <span className="flex items-center gap-1">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="text-[11px] border border-slate-200 rounded-md px-1.5 py-0.5" data-testid="trend-custom-from" />
              <span className="text-slate-400 text-[10px]">→</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="text-[11px] border border-slate-200 rounded-md px-1.5 py-0.5" data-testid="trend-custom-to" />
            </span>
          )}
          <span className="ml-auto flex items-center gap-1.5 flex-wrap justify-end">
            <label className="flex items-center gap-1 text-[10px] text-slate-500 select-none" title="Group series by salesperson">
              <span>Group:</span>
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} data-testid="trend-group-by"
                className="text-[10px] border border-slate-200 rounded-md px-1.5 py-0.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-400">
                <option value="">None (total)</option>
                <option value="salesperson">Salesperson</option>
              </select>
            </label>
            {groupBy === 'salesperson' && (
              <label className="flex items-center gap-1 text-[10px] text-slate-500 select-none" title="How many salespeople to plot individually">
                <span>Show:</span>
                <select value={topN} onChange={(e) => setTopN(parseInt(e.target.value, 10))} data-testid="trend-top-n"
                  className="text-[10px] border border-slate-200 rounded-md px-1.5 py-0.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-400">
                  <option value={5}>Top 5 + Others</option>
                  <option value={10}>Top 10 + Others</option>
                  <option value={0}>All</option>
                </select>
              </label>
            )}
            <label className="flex items-center gap-1 text-[10px] text-slate-500 select-none" title="Exclude one client type from the trend">
              <MultiPicker label="Client Type" testid="trend-include-client-type" width="w-32"
                options={data?.client_types || []} value={includeClientType} onChange={setIncludeClientType}
                accent="violet" />
            </label>
            <label className="flex items-center gap-1 text-[10px] text-slate-500 select-none" title="Restrict trend to selected salespeople">
              <MultiPicker label="Salesperson" testid="trend-include-salesperson" width="w-40"
                options={data?.salespeople || []} value={includeSalesperson} onChange={setIncludeSalesperson}
                accent="violet" />
            </label>
            <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-0.5">Chart</span>
            {CHART_TYPES.map((c) => (
              <button key={c.id} type="button" onClick={() => setChartType(c.id)}
                data-testid={`trend-chart-${c.id}`}
                className={`text-[11px] px-2 py-1 rounded-md border transition ${chartType === c.id ? 'bg-slate-800 text-white border-slate-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                <span className="mr-1">{c.icon}</span>{c.label}
              </button>
            ))}
            <button type="button" onClick={exportCsv} data-testid="trend-export-csv"
              className="text-[11px] px-2.5 py-1 rounded-md border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 flex items-center gap-1">
              <Download className="w-3 h-3" /> CSV
            </button>
            <button type="button" onClick={exportPng} data-testid="trend-export-png"
              className="text-[11px] px-2.5 py-1 rounded-md border border-violet-300 text-violet-700 bg-violet-50 hover:bg-violet-100 flex items-center gap-1">
              <FileText className="w-3 h-3" /> PNG
            </button>
          </span>
        </div>

        {s && (
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
              <p className="text-[9px] uppercase tracking-wide text-emerald-600 font-semibold">Total received</p>
              <p className="text-sm font-bold text-emerald-800 tabular-nums">{fmtInr(s.total)} <span className="text-[10px] font-normal text-slate-500">· {s.count} pmt</span></p>
            </div>
            <div className="bg-sky-50 border border-sky-100 rounded-lg px-3 py-2">
              <p className="text-[9px] uppercase tracking-wide text-sky-600 font-semibold">Average / day</p>
              <p className="text-sm font-bold text-sky-800 tabular-nums">{fmtInr(avg)}</p>
            </div>
            <div className="bg-violet-50 border border-violet-100 rounded-lg px-3 py-2">
              <p className="text-[9px] uppercase tracking-wide text-violet-600 font-semibold">Peak day</p>
              <p className="text-sm font-bold text-violet-800 tabular-nums">{fmtInr(s.peak_day?.total || 0)}</p>
              <p className="text-[9px] text-slate-500">{s.peak_day?.date ? dayLabel(s.peak_day.date) : '—'}</p>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <p className="text-[9px] uppercase tracking-wide text-amber-600 font-semibold">Days with $0</p>
              <p className="text-sm font-bold text-amber-800 tabular-nums">{data.series.filter((d) => d.total === 0).length}</p>
              <p className="text-[9px] text-slate-500">of {data.series.length} days</p>
            </div>
          </div>
        )}

        <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid="payments-trend-chart">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-violet-600" /></div>
          ) : !data ? null : (() => {
            const groups = data.groups || [];
            const grouped = groups.length > 0;
            const yTick = (v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v;
            const xAxis = <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />;
            const yAxis = <YAxis tickFormatter={yTick} tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} width={50} />;
            const grid = <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />;
            const refLine = avg > 0 && !grouped ? (
              <ReferenceLine y={avg} stroke="#f59e0b" strokeDasharray="4 4" strokeOpacity={0.6}
                label={{ value: `avg ${fmtInr(avg)}`, position: 'insideTopRight', fontSize: 9, fill: '#b45309' }} />
            ) : null;
            const tip = <Tooltip content={<ChartTooltip groups={groups} />} cursor={chartType === 'bar' ? { fill: 'rgba(16, 185, 129, 0.08)' } : { stroke: '#a3a3a3', strokeDasharray: '3 3' }} />;

            return (
              <ResponsiveContainer width="100%" height={320}>
                {chartType === 'area' ? (
                  <AreaChart data={data.series} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                    {!grouped && (
                      <defs>
                        <linearGradient id="trend-fill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                    )}
                    {grid}{xAxis}{yAxis}{tip}{refLine}
                    {grouped ? groups.map((g, i) => (
                      <Area key={g} type="monotone" dataKey={g} stackId="1" name={g}
                        stroke={colorFor(g, i)} fill={colorFor(g, i)} fillOpacity={0.55} strokeWidth={1.4} />
                    )) : (
                      <Area type="monotone" dataKey="total" stroke="#059669" strokeWidth={2.4} fill="url(#trend-fill)"
                        activeDot={{ r: 4, stroke: '#065f46', strokeWidth: 2, fill: '#fff' }} />
                    )}
                  </AreaChart>
                ) : chartType === 'line' ? (
                  <LineChart data={data.series} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                    {grid}{xAxis}{yAxis}{tip}{refLine}
                    {grouped ? groups.map((g, i) => (
                      <Line key={g} type="monotone" dataKey={g} name={g} stroke={colorFor(g, i)} strokeWidth={2}
                        dot={{ r: 2.5, stroke: colorFor(g, i), strokeWidth: 1.2, fill: '#fff' }}
                        activeDot={{ r: 4, stroke: colorFor(g, i), strokeWidth: 2, fill: '#fff' }} />
                    )) : (
                      <Line type="monotone" dataKey="total" stroke="#059669" strokeWidth={2.4}
                        dot={{ r: 3, stroke: '#059669', strokeWidth: 1.5, fill: '#fff' }}
                        activeDot={{ r: 5, stroke: '#065f46', strokeWidth: 2, fill: '#fff' }} />
                    )}
                  </LineChart>
                ) : (
                  <BarChart data={data.series} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                    {grid}{xAxis}{yAxis}{tip}{refLine}
                    {grouped ? groups.map((g, i) => (
                      <Bar key={g} dataKey={g} name={g} stackId="1" fill={colorFor(g, i)}
                        radius={i === groups.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} maxBarSize={40} />
                    )) : (
                      <Bar dataKey="total" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} />
                    )}
                  </BarChart>
                )}
              </ResponsiveContainer>
            );
          })()}
          {data && (data.groups?.length > 0) && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 pt-2 border-t border-slate-100" data-testid="trend-legend">
              {data.groups.map((g, i) => (
                <span key={g} className="flex items-center gap-1 text-[10px] text-slate-600" title={g === 'Others' && data.others_members?.length ? data.others_members.join(', ') : ''}>
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: colorFor(g, i) }} />
                  {g}
                </span>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentsTrendModal;
